import React, { useContext, useEffect, useState } from 'react';
import GameContext from 'contexts/GameContext';
import GlobalContext from 'contexts/GlobalContext';
import { useAppSelector } from 'hooks';
import { useGuest } from 'hooks/useGuest';
import { useJoinWaitlist } from 'hooks/useJoinWaitlist';
import { useLeaveTable } from 'hooks/useLeaveTable';
import { useSupportsHover } from 'hooks/useSupportsHover';
import { useTableId, useTableSelector } from 'hooks/useTableSelector';
import {
  selectIsQueuedFor,
  selectMySpotHasValidOffer,
  selectMySpotIsAutoSeat
} from 'reducers/waitlist.selectors';
import useGameService from 'services/GameService';
import {
  saloonHoverScaleIn,
  saloonHoverScaleOut,
  saloonMutedButtonStyle
} from 'styles/saloonButtons';
import type { WaitlistGameType } from 'types/waitlist';
import { isWaitlistGameType } from 'types/waitlist';
import {
  consumeJoinIntent,
  markJoinIntentBeforeLogin,
  peekJoinIntent,
  redirectToLogin
} from 'utils/auth';
import { GUEST_CTA } from 'utils/guestCta';

import JoinWaitlistButton from 'components/Waitlist/JoinWaitlistButton';
import LeaveWaitlistConfirmModal from 'components/Waitlist/LeaveWaitlistConfirmModal';

import SaloonJoinCta from './SaloonJoinCta';

/**
 * CTA rendered inside TopAction's center slot. Branch evaluation order and
 * every gate mirror Unity TablesView.cs:2211-2264 exactly.
 *
 * Unity branches, in order:
 *  1. Private table: SetActive(canJoinPrivateTable && IsShowJoinButton())
 *  2. Seat offer (CONFIRM_SEAT_BUTTON = "Join Table"):
 *       IsYourTurn && !IsAutoSeat && !IsCurrentPlayerSeated
 *       cash-only via SetActive(TableType == TABLE_TYPE_CASH = "s")
 *  3. Full cash / waitlist present:
 *       TableType == cash && (IsTableFull || WaitlistCount > 0) && !IsSeated
 *       splits on IsGameIdInWaitlist → "Exit Waitlist" (gray) or "Join Waitlist"
 *  4. Normal: IsShowJoinButton() → "Join Table" / "Rejoin Table"
 *  5. Else: hide
 *
 * Key definitions (all from Unity):
 *   - IsTableFull    = PlayersCount >= Seats   (TableModel.cs:395)
 *   - IsYourTurn     = yourTurn && availableSeats > 0 (WaitListController.cs:45)
 *   - IsAutoSeat     = autoBuyIn > 0           (WaitListController.cs:48)
 *   - IsShowJoinButton: JoinButtonVisible && ((!IsSeated && cash && PlayersCount < Seats)
 *                        || IsWaitingAndExit || IsRejoining)  (TableController.cs:472)
 */
const JoinButton = () => {
  const { imBack } = useGameService();
  const { setOpenAskBuyin, setOpenAskPassword, setAskBuyinTable } = useContext(GlobalContext);
  const { setIsSatOut, setIsStandingUp } = useContext(GameContext);
  // Read this table's state via TableIdContext, not the global currentTable
  // slice. In multi-table grid view every cell mounts its own JoinButton; a
  // global read would make all cells reflect whichever table last pushed an
  // update, so the button flickered on/off as currentTable thrashed between
  // tables. useTableSelector resolves the cell's own table (and falls back to
  // currentTable in single-table view), matching the sibling action components.
  const {
    hand,
    waitlistCount,
    privateTable,
    canJoinPrivateTable,
    gameID,
    game: gameVariant,
    gameType: gameTypeField,
    seats,
    tabletype,
    game
  } = useTableSelector((table) => table.game);
  const contextTableId = useTableId();
  const seatId = useTableSelector((table) => table.currentPlayer.seatId);
  const isSeated = useTableSelector((table) => table.currentPlayer.isSeated);
  // joinButtonVisible is a client-side UI gate maintained ONLY on the global
  // currentTable slice — it is set by the showJoinButton action, which
  // UserActions dispatches without a meta.gameID, so it never reaches the
  // per-cell state.tables[gameID] entry (those are populated only by
  // engine messages carrying meta.gameID). Reading it per-cell would always
  // see the initial `false` and suppress the Join Table CTA in grid cells.
  // It only toggles false transiently during leaveTable, so reading it
  // globally is stable and does not reintroduce the cross-table flicker.
  const joinButtonVisible = useAppSelector(
    (state) => state.currentTable.currentPlayer.joinButtonVisible
  );
  // Unity PlayersCount — engine field "noPlayers" (GameStateData.cs:8).
  const playersCount = useTableSelector((table) => table.gameState.noPlayers);
  const isWaitingAndExit = useTableSelector((table) => table.currentPlayer.isWaitingAndExit);
  const isRejoining = useTableSelector((table) => table.currentPlayer.isRejoining);
  const playerGuid = useAppSelector((state) => state.user.playerGUID) || '';
  const numericGameId = Number(gameID);
  const isYourTurn = useAppSelector(selectMySpotHasValidOffer(numericGameId));
  const isAutoSeat = useAppSelector(selectMySpotIsAutoSeat(numericGameId));
  const isQueued = useAppSelector(selectIsQueuedFor(numericGameId));
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  // Leaving the waitlist from this modal must land in the same place as leaving
  // it from the Exit Waitlist button's inline confirm — the SAME hook, not a
  // re-implementation. Dropping the table slice alone isn't enough: the client
  // is still subscribed, so the engine's next broadcast re-hydrates the table
  // and it reappears in the strip a moment after vanishing. leaveTable() inside
  // this hook is what emits the unsubscribe and records the exit so the
  // auto-resubscribe leaves it alone.
  const { leave } = useLeaveTable();
  const supportsHover = useSupportsHover();
  const { join: joinWaitlist } = useJoinWaitlist();

  // Unity selects gameType from GameData.GameType, not the variant enum.
  // Fall back through gameType → gameVariant → null so we can still dispatch
  // REST calls that require 'texas' | 'omaha'. Hoisted above the early
  // returns below (render still reads this same value further down) so the
  // post-login auto-effect — which sits before those returns — can use it too.
  const normalizedGameType: WaitlistGameType | null = isWaitlistGameType(gameTypeField)
    ? gameTypeField
    : isWaitlistGameType(gameVariant)
    ? gameVariant
    : null;

  // Scale-only hover, attached only where a real hover pointer exists so
  // touch devices keep their first-tap click (see useSupportsHover).
  const hoverProps = supportsHover
    ? { onMouseEnter: saloonHoverScaleIn, onMouseLeave: saloonHoverScaleOut }
    : {};

  // Guests can't sit — the Join CTA sends them straight to sign in / sign up.
  const guest = useGuest();

  const triggerFunc = () => {
    if (guest) {
      markJoinIntentBeforeLogin(String(gameID));
      redirectToLogin();
      return;
    }
    if (isWaitingAndExit || isRejoining) {
      setIsSatOut(false);
      setIsStandingUp(false);
      imBack(seatId);
    } else {
      // Bind WHICH table this click was for before any modal opens — the
      // buy-in (and the password modal that can precede it) are app-level
      // chrome shared by every keep-alive panel, and an unbound open let the
      // wrong panel's modal answer (owner RCA, 8/25).
      // Bind the type only when actually KNOWN. game.game initialises to '' in
      // an unhydrated slice, and a defaulted 'texas' here is a non-empty value
      // that would WIN over better downstream resolution — turning a click on
      // an unhydrated omaha table into a texas-routed join (SQE scan, Marlon
      // 8/25 HIGH). '' falls through buildJoinGameParams' sentinel instead.
      setAskBuyinTable({ gameID: String(contextTableId || gameID), game: game || '' });
      if (privateTable) {
        setOpenAskPassword(true);
      } else {
        setOpenAskBuyin(true);
      }
      setIsSatOut(false);
      setIsStandingUp(false);
    }
  };

  // A guest's "Login/Sign up to Join" click stashes this table's gameID (see
  // markJoinIntentBeforeLogin above) and sends them to login. utils/auth's
  // consumeLoginReturnPath then hard-reloads them straight back to this table
  // once authenticated. Finish what they came here for — replay whichever
  // real action a click would have run — instead of making them click again.
  // Guarded the same way the render below hides the button entirely: already
  // seated elsewhere, or a tournament that auto-seats on its own.
  //
  // playerGuid and gameID both gate: `guest` flips false the instant the
  // reload sees a valid auth token — synchronous, no round trip — but
  // isSeated/seats/waitlistCount/etc. only become trustworthy once BOTH the
  // legacy engine's GOT_LOGIN response (resolves identity — see
  // updateCurrentPlayerGUID) and the table-data socket/EventBridge snapshot
  // (resolves capacity) land. Those are two independent, unordered
  // connections — sometimes identity resolves first, sometimes table data
  // does, with no guaranteed order between them. Deciding the instant they
  // FIRST look ready is exactly what made this intermittent: whichever
  // socket happened to be slower on a given load left the OTHER one's data
  // stale at that exact render.
  //
  // So this doesn't decide once — it debounces. Every render where the
  // intent still matches this gameID reschedules the decision; only once
  // every relevant field below has stopped changing for a full quiet window
  // does it actually commit (consume the one-shot stash + act), using
  // whichever values are current at that point. peekJoinIntent (unlike
  // consumeJoinIntent) never clears the stash, so a mismatched gameID or an
  // in-flight debounce can't lose the intent before it's ready to commit.
  useEffect(() => {
    if (guest) return;
    if (!playerGuid) return;
    if (!peekJoinIntent(String(gameID))) return;

    const timer = setTimeout(() => {
      if (!consumeJoinIntent(String(gameID))) return;
      if (isSeated && !isWaitingAndExit && !isRejoining) return;
      if (tabletype === 'm' || tabletype === 't') return;
      //
      // Deliberately NOT checking joinButtonVisible here even though
      // canShowJoinButton does: it defaults to false in the reducer and only
      // flips true via UserActions' own effect reacting to this same gameID —
      // a sibling component, so its effect runs in a LATER commit than this
      // one. Gating on it here would consume the one-shot intent while it's
      // still false and never get a second chance once it flips, silently
      // blocking every not-yet-seated auto-join. It exists to blank the button
      // during leaveTable's transient window, which a guest who's never sat
      // here can't be in the middle of.
      const isCashTableNow = tabletype === 's';
      const seatsNum = Number(seats) || 0;
      const playersNum = Number(playersCount) || 0;
      const isFullNow = isCashTableNow && seatsNum > 0 && playersNum >= seatsNum;
      // Branch 3's equivalent (render, below): a full/waitlisted cash table's
      // real CTA is "Join Waitlist" — a completely different action from
      // triggerFunc()'s buy-in flow, dispatched via useJoinWaitlist rather than
      // opening a modal. A guest's click on ANY branch funnels through the same
      // triggerFunc() (the stash only remembers gameID, not which CTA rendered
      // it), so replicate the same branch selection the render uses to decide
      // what a real click would do right now, instead of just refusing to act
      // on a table that filled up since the guest clicked.
      if (isCashTableNow && (isFullNow || waitlistCount > 0) && !isWaitingAndExit && !isRejoining) {
        if (!isQueued && normalizedGameType) {
          joinWaitlist({ gameId: numericGameId, gameType: normalizedGameType });
        }
        // Already queued, or no dispatchable gameType — nothing to auto-trigger.
        return;
      }
      triggerFunc();
    }, 400);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    guest,
    gameID,
    playerGuid,
    isSeated,
    isWaitingAndExit,
    isRejoining,
    tabletype,
    seats,
    playersCount,
    waitlistCount,
    isQueued,
    normalizedGameType
  ]);

  // Don't show anything if the player is already actively seated.
  // Exception: players in a "left game" state (status 10 / 17 / 18) still
  // hold a seatId > 0 — so `isSeated` is true — but they need this button
  // back as their "Rejoin Table" CTA. Without the carve-out the button
  // disappeared after exit, leaving the leaver with no way back to their
  // seat from the table view (the seat still shows "LEFT GAME" on the
  // avatar but had no clickable rejoin path).
  if (isSeated && !isWaitingAndExit && !isRejoining) return null;

  // Tournament tables (MTT "m", SNG "t") don't expose a manual rejoin —
  // the engine re-seats players automatically. Bail before any branch
  // below considers showing a Join / Rejoin CTA on a tournament surface.
  if (tabletype === 'm' || tabletype === 't') return null;

  // Unity TABLE_TYPE_CASH = "s" (Constants/GameConstants.cs:7). Same
  // discriminator is used across v2 — see LobbyHeader, ActiveGamesBar,
  // LobbyTable etc. MTT is "m", SNG is "t".
  const isCashTable = tabletype === 's';
  // The engine can deliver these as strings (Game.tsx:99-100 coerces the same
  // fields); compare numerically so isTableFull / the join gate never go
  // lexicographic or treat '' as a number.
  const seatsNum = Number(seats) || 0;
  const playersNum = Number(playersCount) || 0;
  // Unity IsTableFull — engine-authoritative PlayersCount >= Seats.
  const isTableFull = seatsNum > 0 && playersNum >= seatsNum;
  // Unity IsShowJoinButton (TableController.cs:472-488).
  const canShowJoinButton =
    joinButtonVisible &&
    ((!isSeated && isCashTable && playersNum < seatsNum) || isWaitingAndExit || isRejoining);
  const isRejoinLabel = seatId > 0 && (isWaitingAndExit || isRejoining);
  // For guests every join CTA reads "Sign up to Join"; members keep the
  // contextual Join/Rejoin labels per branch.
  const guestJoinLabel = GUEST_CTA.cash;

  const clickable = hand === '' || +hand > 3;
  // --- Branch 1: private table ------------------------------------------
  // Unity TablesView.cs:2212-2226
  if (privateTable === 1) {
    if (!canJoinPrivateTable || !canShowJoinButton) return null;
    const privateLabel = guest
      ? guestJoinLabel
      : isRejoinLabel
      ? 'Rejoin Table'
      : 'Join Private Table';
    return (
      <SaloonJoinCta
        ariaLabel='private-join-cta'
        label={privateLabel}
        onClick={() => clickable && triggerFunc()}
      />
    );
  }

  // --- Branch 2: seat offer (CONFIRM_SEAT_BUTTON = "Join Table") --------
  // Unity TablesView.cs:2229-2236.
  if (isYourTurn && !isAutoSeat && !isSeated && isCashTable) {
    return (
      <SaloonJoinCta
        ariaLabel='accept-seat-cta'
        label={guest ? guestJoinLabel : 'Join Table'}
        onClick={() => clickable && triggerFunc()}
      />
    );
  }

  // --- Branch 3: full cash table or waitlist present --------------------
  // Unity TablesView.cs:2237-2254.
  if (isCashTable && (isTableFull || waitlistCount > 0) && !isSeated) {
    // Guests can't join a waitlist — show a sign-in CTA that routes to login,
    // instead of the Join Waitlist / Set Buy-In / Exit Waitlist controls.
    if (guest) {
      return (
        <SaloonJoinCta
          ariaLabel='guest-waitlist-cta'
          label={GUEST_CTA.waitlist}
          onClick={() => clickable && triggerFunc()}
        />
      );
    }
    if (isQueued && normalizedGameType) {
      return (
        <div style={queuedRowStyle}>
          {/* While queued we show only Exit Waitlist. (The pre-set-buy-in CTA
              was removed — a waitlisted player sets their buy-in when the seat
              is actually offered.) */}
          <button
            type='button'
            aria-label='exit-waitlist-cta'
            style={{ ...saloonMutedButtonStyle, ...queuedPillStyle }}
            onClick={() => setLeaveModalOpen(true)}
            {...hoverProps}
          >
            Exit Waitlist
          </button>
          <LeaveWaitlistConfirmModal
            open={leaveModalOpen}
            gameId={numericGameId}
            gameType={normalizedGameType}
            playerGuid={playerGuid}
            onClose={() => setLeaveModalOpen(false)}
            // Fires only after the waitlist DELETE succeeds, so a failed leave
            // leaves the table exactly where it was. skipWaitlistExit because
            // the modal has just done that half itself — a second DELETE would
            // be pointless noise.
            onLeft={() => leave({ skipWaitlistExit: true })}
          />
        </div>
      );
    }
    if (normalizedGameType) {
      return <JoinWaitlistButton gameId={numericGameId} gameType={normalizedGameType} />;
    }
    // Cash table, full, but we don't have a normalized gameType to dispatch
    // with — mirror Unity's "nothing actionable" behaviour and hide.
    return null;
  }

  // --- Branch 4: normal join / rejoin -----------------------------------
  // Unity TablesView.cs:2255-2260.
  if (canShowJoinButton) {
    const label = guest ? guestJoinLabel : isRejoinLabel ? 'Rejoin Table' : 'Join Table';
    return (
      <SaloonJoinCta
        ariaLabel='join-table-cta'
        label={label}
        onClick={() => clickable && triggerFunc()}
      />
    );
  }

  // --- Branch 5: hide ---------------------------------------------------
  // Unity TablesView.cs:2261-2263.
  return null;
};

export default JoinButton;

// Holds the Set Buy-In and Exit Waitlist pills side by side while queued.
// nowrap keeps them on a single row — they must never stack vertically, even
// on narrow viewports where wrapping would otherwise push one pill below.
const queuedRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
  flexWrap: 'nowrap',
  // Fill the (flex, minWidth:0) center action slot so the pills shrink WITHIN it
  // rather than overflowing under the flanking history / chat icons in portrait.
  width: '100%',
  minWidth: 0
};

// In the queued row the two pills must share the narrow center slot (esp. mobile
// portrait). Let them shrink below their intrinsic width and ellipsize instead
// of keeping full width and spilling under the corner icons.
const queuedPillStyle: React.CSSProperties = {
  flex: '1 1 0',
  minWidth: 0,
  maxWidth: '180px',
  padding: '0.7rem 1rem',
  fontSize: '0.9rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};
